import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchAuditLog, fetchTenants } from '../../services/adminApi';

const ACTION_COLORS = {
    CREATE:     { bg: '#dcfce7', text: '#166534', border: '#86efac' },
    UPDATE:     { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
    ACTIVATE:   { bg: '#d1fae5', text: '#065f46', border: '#6ee7b7' },
    DEACTIVATE: { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
    DELETE:     { bg: '#fee2e2', text: '#991b1b', border: '#fca5a5' },
};

const ENTITY_LABELS = {
    tenant_portfolio_levels: 'Portfolio Levels',
    tenant_fund_allocations: 'Fund Allocations',
    tenant_risk_bands:       'Risk Bands',
    tenant_funds:            'Available Funds',
    tenants:                 'Tenant',
    fund_providers:          'Provider',
};

const ACTION_OPTIONS  = ['CREATE', 'UPDATE', 'ACTIVATE', 'DEACTIVATE', 'DELETE'];
const ENTITY_OPTIONS  = Object.keys(ENTITY_LABELS);

function ActionBadge({ action }) {
    const c = ACTION_COLORS[action] || { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' };
    return (
        <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 4,
            fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
            background: c.bg, color: c.text, border: `1px solid ${c.border}`,
        }}>{action}</span>
    );
}

function JsonBlock({ label, data }) {
    return (
        <div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
            {data == null
                ? <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>—</div>
                : <pre style={{
                    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6,
                    padding: '10px 12px', fontSize: 11, margin: 0, maxHeight: 280,
                    overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>{JSON.stringify(data, null, 2)}</pre>
            }
        </div>
    );
}

function AuditLog() {
    const [tenantOptions, setTenantOptions] = useState([]);
    const [entries, setEntries] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);

    const [filters, setFilters] = useState({
        tenantCode: '', entityType: '', action: '', actorEmail: '', fromDate: '', toDate: '',
    });

    useEffect(() => {
        fetchTenants()
            .then(d => setTenantOptions((d.tenants || []).filter(x => x.active !== false).map(x => ({ code: x.code, name: x.name }))))
            .catch(() => {});
    }, []);

    const load = useCallback((p = 1) => {
        setLoading(true);
        setError(null);
        fetchAuditLog({ ...filters, page: p, perPage: 50 })
            .then(data => {
                setEntries(data.entries || []);
                setTotal(data.total || 0);
                setPages(data.pages || 0);
                setPage(p);
                setExpandedId(null);
            })
            .catch(() => setError('Failed to load audit log.'))
            .finally(() => setLoading(false));
    }, [filters]);

    useEffect(() => { load(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSearch = (e) => { e.preventDefault(); load(1); };

    const fmtDate = (s) => {
        if (!s) return '—';
        return new Date(s).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'medium' });
    };

    return (
        <div className="page-content">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Audit Log</h1>
                    <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                        All administrative changes — tenant/provider CRUD and portfolio config mutations.
                    </p>
                </div>
                <Button variant="outline-secondary" size="sm" onClick={() => load(page)}>
                    <FontAwesomeIcon icon="rotate-right" className="me-1" /> Refresh
                </Button>
            </div>

            {error && <Alert variant="danger" className="mb-3" dismissible onClose={() => setError(null)}>{error}</Alert>}

            {/* Filters */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="py-3">
                    <form onSubmit={handleSearch}>
                        <div className="row g-2 align-items-end">
                            <div className="col-md-2">
                                <label className="form-label mb-1" style={{ fontSize: 12 }}>Tenant</label>
                                <Form.Select size="sm" value={filters.tenantCode}
                                    onChange={e => setFilters(p => ({ ...p, tenantCode: e.target.value }))}>
                                    <option value="">All tenants</option>
                                    {tenantOptions.map(t => <option key={t.code} value={t.code}>{t.code}</option>)}
                                </Form.Select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label mb-1" style={{ fontSize: 12 }}>Entity type</label>
                                <Form.Select size="sm" value={filters.entityType}
                                    onChange={e => setFilters(p => ({ ...p, entityType: e.target.value }))}>
                                    <option value="">All types</option>
                                    {ENTITY_OPTIONS.map(v => <option key={v} value={v}>{ENTITY_LABELS[v]}</option>)}
                                </Form.Select>
                            </div>
                            <div className="col-md-1">
                                <label className="form-label mb-1" style={{ fontSize: 12 }}>Action</label>
                                <Form.Select size="sm" data-testid="audit-action-filter" value={filters.action}
                                    onChange={e => setFilters(p => ({ ...p, action: e.target.value }))}>
                                    <option value="">All</option>
                                    {ACTION_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                                </Form.Select>
                            </div>
                            <div className="col-md-2">
                                <label className="form-label mb-1" style={{ fontSize: 12 }}>Actor email</label>
                                <input type="text" className="form-control form-control-sm" placeholder="Search…"
                                    value={filters.actorEmail}
                                    onChange={e => setFilters(p => ({ ...p, actorEmail: e.target.value }))} />
                            </div>
                            <div className="col-md-1">
                                <label className="form-label mb-1" style={{ fontSize: 12 }}>From</label>
                                <input type="date" className="form-control form-control-sm"
                                    value={filters.fromDate}
                                    onChange={e => setFilters(p => ({ ...p, fromDate: e.target.value }))} />
                            </div>
                            <div className="col-md-1">
                                <label className="form-label mb-1" style={{ fontSize: 12 }}>To</label>
                                <input type="date" className="form-control form-control-sm"
                                    value={filters.toDate}
                                    onChange={e => setFilters(p => ({ ...p, toDate: e.target.value }))} />
                            </div>
                            <div className="col-md-3 d-flex gap-2 align-items-end">
                                <Button type="submit" variant="primary" size="sm">Search</Button>
                                <Button type="button" variant="outline-secondary" size="sm" onClick={() =>
                                    setFilters({ tenantCode: '', entityType: '', action: '', actorEmail: '', fromDate: '', toDate: '' })
                                }>Clear</Button>
                            </div>
                        </div>
                    </form>
                </Card.Body>
            </Card>

            {/* Results table */}
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="d-flex justify-content-center p-5">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <>
                            <div className="d-flex align-items-center justify-content-between px-4 py-2 border-bottom"
                                style={{ background: '#f8fafc', fontSize: 12, color: '#64748b' }}>
                                <span>{total.toLocaleString()} {total === 1 ? 'entry' : 'entries'}</span>
                                {pages > 1 && (
                                    <div className="d-flex align-items-center gap-2">
                                        <span>Page {page} of {pages}</span>
                                        <Button variant="outline-secondary" size="sm" disabled={page <= 1}
                                            onClick={() => load(page - 1)}
                                            style={{ padding: '2px 8px', fontSize: 12 }}>‹</Button>
                                        <Button variant="outline-secondary" size="sm" disabled={page >= pages}
                                            onClick={() => load(page + 1)}
                                            style={{ padding: '2px 8px', fontSize: 12 }}>›</Button>
                                    </div>
                                )}
                            </div>
                            <table className="table table-sm table-hover mb-0" style={{ fontSize: 12 }}>
                                <thead className="table-light">
                                    <tr>
                                        <th className="px-4" style={{ minWidth: 140 }}>Timestamp</th>
                                        <th className="px-3" style={{ minWidth: 160 }}>Actor</th>
                                        <th className="px-3" style={{ minWidth: 110 }}>Action</th>
                                        <th className="px-3" style={{ minWidth: 150 }}>Entity type</th>
                                        <th className="px-3" style={{ minWidth: 80 }}>Tenant</th>
                                        <th className="px-3" style={{ minWidth: 100 }}>Entity ID</th>
                                        <th style={{ width: 28 }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!entries.length && (
                                        <tr>
                                            <td colSpan={7} className="text-center text-muted py-5">
                                                No audit entries found.
                                            </td>
                                        </tr>
                                    )}
                                    {entries.map(entry => (
                                        <React.Fragment key={entry.id}>
                                            <tr
                                                onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                                                style={{ cursor: 'pointer' }}
                                                className={expandedId === entry.id ? 'table-active' : ''}
                                            >
                                                <td className="px-4" style={{ fontFamily: 'monospace', color: '#374151' }}>
                                                    {fmtDate(entry.created_at)}
                                                </td>
                                                <td className="px-3" style={{ color: '#374151' }}>
                                                    {entry.actor_email || '—'}
                                                </td>
                                                <td className="px-3">
                                                    <ActionBadge action={entry.action} />
                                                </td>
                                                <td className="px-3" style={{ color: '#374151' }}>
                                                    {ENTITY_LABELS[entry.entity_type] || entry.entity_type}
                                                </td>
                                                <td className="px-3">
                                                    {entry.tenant_code
                                                        ? <span className="badge bg-light text-dark border" style={{ fontSize: 11 }}>{entry.tenant_code}</span>
                                                        : <span className="text-muted">—</span>}
                                                </td>
                                                <td className="px-3" style={{ fontFamily: 'monospace', color: '#94a3b8', fontSize: 11 }}>
                                                    {entry.entity_id ? `${entry.entity_id.slice(0, 8)}…` : '—'}
                                                </td>
                                                <td className="text-center" style={{ color: '#94a3b8' }}>
                                                    <FontAwesomeIcon
                                                        icon={expandedId === entry.id ? 'chevron-down' : 'chevron-right'}
                                                        style={{ fontSize: 10 }} />
                                                </td>
                                            </tr>
                                            {expandedId === entry.id && (
                                                <tr>
                                                    <td colSpan={7} className="px-4 py-3" style={{ background: '#f8fafc' }}>
                                                        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10, fontFamily: 'monospace' }}>
                                                            id: {entry.entity_id} &nbsp;·&nbsp; ip: {entry.ip_address || '—'}
                                                        </div>
                                                        <div className="row g-3">
                                                            <div className="col-md-6">
                                                                <JsonBlock label="Before" data={entry.payload_before} />
                                                            </div>
                                                            <div className="col-md-6">
                                                                <JsonBlock label="After" data={entry.payload_after} />
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
}

export default AuditLog;
